import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/api";
import { PageTitle } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Network, Search, FileCheck2, CheckCircle2, XCircle, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const sel = "px-2 py-1.5 rounded-lg bg-white border border-sky-300/50 text-xs focus:outline-none";

const OfficialDialog = ({ onClose, onApplied }) => {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.post("/school-distribution/official").then((r) => setRep(r.data.report)).catch((e) => toast.error(apiError(e))); }, []);
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/school-distribution/official?apply=true");
      const n = r.data.report.reduce((t, x) => t + (x.supervisor_found ? x.matched.length : 0), 0);
      toast.success(`تم تطبيق التوزيع الرسمي: ربط ${n} مدرسة`);
      onApplied(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-3xl max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="official-dialog">
        <DialogHeader><DialogTitle className="gold-text font-head flex items-center gap-2"><FileCheck2 size={18} /> معاينة التوزيع الرسمي قبل التطبيق</DialogTitle></DialogHeader>
        {!rep ? <div className="text-center py-6 text-stone-500">جارٍ المطابقة...</div> : (
          <div className="space-y-3">
            {rep.map((x) => (
              <div key={x.supervisor} data-testid={`official-${x.supervisor}`} className="rounded-xl border border-sky-200 bg-white/80 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold">{x.supervisor_name || x.supervisor} <span className="text-xs text-stone-500">({x.type})</span></div>
                  {x.supervisor_found
                    ? <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13} /> {x.matched.length} مدرسة مطابقة</span>
                    : <span className="text-xs text-red-700 flex items-center gap-1"><XCircle size={13} /> حساب المشرف غير موجود — لن يُطبّق</span>}
                </div>
                {x.moved.length > 0 && <div className="text-[11px] text-amber-700 mt-1">سيُنقل من مشرف آخر: {x.moved.map((m) => `${m.school_name} (من ${m.from})`).join("، ")}</div>}
                {x.unmatched.length > 0 && <div className="text-[11px] text-red-700 mt-1" data-testid={`official-unmatched-${x.supervisor}`}>غير مطابقة (اربطها يدوياً): {x.unmatched.map((u) => `${u.name} — ${u.reason}`).join("، ")}</div>}
                <details className="mt-1"><summary className="text-[11px] text-sky-700 cursor-pointer">عرض المدارس المطابقة</summary>
                  <div className="text-[11px] text-stone-600 leading-6">{x.matched.map((m) => m.school_name).join("، ")}</div></details>
              </div>
            ))}
            <div className="text-xs text-stone-500">يُطبّق الربط على المدارس المطابقة فقط، وكل مدرسة تكون لمشرف واحد. ما سواها يبقى كما هو.</div>
            <button data-testid="official-apply" onClick={apply} disabled={busy} className="btn-gold w-full py-2.5 rounded-xl disabled:opacity-60">{busy ? "جارٍ التطبيق..." : "تطبيق التوزيع الرسمي"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function SchoolDistribution() {
  const { user } = useAuth();
  if (user && (user.role !== "admin" || user.is_supervisor || user.is_monitor)) return <Navigate to="/" replace />;
  return <DistributionInner />;
}

function DistributionInner() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [supF, setSupF] = useState("all");
  const [picked, setPicked] = useState(new Set());
  const [target, setTarget] = useState("");
  const [official, setOfficial] = useState(false);

  const load = () => api.get("/school-distribution").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPicked(new Set()); }, [q, type, supF]);

  const supName = useMemo(() => Object.fromEntries((data?.supervisors || []).map((s) => [s.id, s.name])), [data]);
  const counts = useMemo(() => {
    const c = { none: 0 };
    (data?.schools || []).forEach((s) => { const k = s.supervisor_id || "none"; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [data]);
  const rows = useMemo(() => (data?.schools || []).filter((s) =>
    (type === "all" || s.type === type) && (supF === "all" || (supF === "none" ? !s.supervisor_id : s.supervisor_id === supF)) && (!q.trim() || s.name.includes(q.trim()))
  ), [data, type, supF, q]);
  const allPicked = rows.length > 0 && rows.every((s) => picked.has(s.id));

  const assign = async (ids, supervisor_id, keepPicks = false) => {
    try {
      const r = await api.post("/school-distribution/assign", { school_ids: ids, supervisor_id });
      toast.success(supervisor_id ? `نُقلت ${r.data.count} مدرسة إلى ${supName[supervisor_id]}` : `أُلغي ربط ${r.data.count} مدرسة`);
      if (!keepPicks) setPicked(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setPicked((p) => { const n = new Set(p); rows.forEach((s) => (allPicked ? n.delete(s.id) : n.add(s.id))); return n; });

  return (
    <div className="space-y-5" data-testid="distribution-page">
      <PageTitle title="توزيع المدارس على المشرفين" subtitle="كل مدرسة لمشرف واحد — بدّل المشرف من القائمة بجانب المدرسة أو انقل عدة مدارس دفعة واحدة" icon={Network}
        actions={<button data-testid="official-open" onClick={() => setOfficial(true)} className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-1"><FileCheck2 size={15} /> تطبيق التوزيع الرسمي</button>} />

      <div className="flex flex-wrap gap-2" data-testid="dist-summary">
        <button data-testid="dist-sup-all" onClick={() => setSupF("all")} className={`px-3 py-2 rounded-xl text-xs border ${supF === "all" ? "bg-sky-600 text-white border-sky-600" : "bg-white border-sky-200"}`}>كل المدارس ({data?.schools.length ?? 0})</button>
        {(data?.supervisors || []).map((s) => (
          <button key={s.id} data-testid={`dist-sup-${s.id}`} onClick={() => setSupF(s.id)} className={`px-3 py-2 rounded-xl text-xs border ${supF === s.id ? "bg-sky-600 text-white border-sky-600" : "bg-white border-sky-200"}`}>{s.name} ({counts[s.id] || 0})</button>
        ))}
        <button data-testid="dist-sup-none" onClick={() => setSupF("none")} className={`px-3 py-2 rounded-xl text-xs border ${supF === "none" ? "bg-red-600 text-white border-red-600" : "bg-white border-red-200 text-red-700"}`}>بلا مشرف ({counts.none})</button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {[["all", "الكل"], ["بنين", "بنين"], ["بنات", "بنات"]].map(([k, l]) => (
          <button key={k} data-testid={`dist-type-${k}`} onClick={() => setType(k)} className={`px-3 py-1.5 rounded-full text-xs border ${type === k ? "bg-amber-600 text-white border-amber-600" : "bg-white border-amber-400/40"}`}>{l}</button>
        ))}
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="dist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم المدرسة..." className="w-full pr-9 px-3 py-2 rounded-xl bg-white border border-sky-300/50 text-sm focus:outline-none" />
        </div>
      </div>

      {picked.size > 0 && (
        <div className="sticky top-2 z-10 rounded-xl bg-sky-50 border border-sky-300 px-3 py-2 flex flex-wrap items-center gap-2 text-sm" data-testid="dist-bulk">
          <ArrowLeftRight size={16} className="text-sky-700" /> المحدد: <b data-testid="dist-picked">{picked.size}</b> — نقل إلى:
          <select data-testid="dist-bulk-target" value={target} onChange={(e) => setTarget(e.target.value)} className={sel}>
            <option value="">اختر المشرف...</option>
            {(data?.supervisors || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            <option value="__none">إلغاء الربط</option>
          </select>
          <button data-testid="dist-bulk-apply" disabled={!target} onClick={() => assign([...picked], target === "__none" ? "" : target)} className="btn-gold px-3 py-1.5 rounded-lg text-xs disabled:opacity-50">تنفيذ</button>
          <button data-testid="dist-bulk-clear" onClick={() => setPicked(new Set())} className="text-xs text-stone-500 underline">إلغاء التحديد</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl bg-white/85 border border-sky-100">
        <table className="w-full text-sm" data-testid="dist-table">
          <thead className="bg-sky-50"><tr className="text-stone-500 text-right">
            <th className="py-2 px-2"><input type="checkbox" data-testid="dist-check-all" checked={allPicked} onChange={toggleAll} /></th>
            <th className="py-2 px-2 font-medium">المدرسة</th><th className="px-2 font-medium">النوع</th><th className="px-2 font-medium">المشرف</th>
          </tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} className={`border-t border-sky-100 ${s.supervisor_id ? "" : "bg-red-50/40"}`} data-testid={`dist-row-${s.id}`}>
              <td className="px-2 py-1.5"><input type="checkbox" data-testid={`dist-check-${s.id}`} checked={picked.has(s.id)} onChange={() => toggle(s.id)} /></td>
              <td className="px-2 text-stone-800">{s.name}</td><td className="px-2 text-xs">{s.type}</td>
              <td className="px-2">
                <select data-testid={`dist-select-${s.id}`} value={s.supervisor_id} onChange={(e) => assign([s.id], e.target.value, true)} className={sel}>
                  <option value="">— بلا مشرف —</option>
                  {(data?.supervisors || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </td>
            </tr>))}
          </tbody>
        </table>
        {data && !rows.length && <div className="text-center text-stone-500 text-sm py-6">لا توجد مدارس مطابقة</div>}
      </div>
      {official && <OfficialDialog onClose={() => setOfficial(false)} onApplied={load} />}
    </div>
  );
}
